import React, { useCallback, useRef } from 'react';
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { Play, Github, Globe, ArrowUpRight } from 'lucide-react';
import { projectCardReveal } from '../motion/presets';
import '../styles/ProjectCard.css';

const MAX_TILT = 3.6;
const PARALLAX_X = 8;
const PARALLAX_Y = 6;

function canUsePointerEffects() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;

    return window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)').matches;
}

export default function ProjectCard({ project, language, t, onClick }) {
    const cardRef = useRef(null);
    const prefersReducedMotion = useReducedMotion();
    const rotateX = useMotionValue(0);
    const rotateY = useMotionValue(0);
    const depthX = useMotionValue(0);
    const depthY = useMotionValue(0);
    const depthScale = useMotionValue(1);
    const smoothRotateX = useSpring(rotateX, { stiffness: 180, damping: 28, mass: 0.9 });
    const smoothRotateY = useSpring(rotateY, { stiffness: 180, damping: 28, mass: 0.9 });
    const smoothDepthX = useSpring(depthX, { stiffness: 160, damping: 30, mass: 1 });
    const smoothDepthY = useSpring(depthY, { stiffness: 160, damping: 30, mass: 1 });
    const smoothDepthScale = useSpring(depthScale, { stiffness: 170, damping: 28, mass: 0.9 });
    const contentX = useTransform(smoothDepthX, (value) => value * -0.28);
    const contentY = useTransform(smoothDepthY, (value) => value * -0.22);
    const title = language === 'tr' ? project.titleTr : project.titleEn;
    const impact = language === 'tr' ? project.impactTr : project.impactEn;
    const label = language === 'tr' ? project.labelTr : project.labelEn;
    const isVideo = project.repoLink && project.repoLink.startsWith('https://youtu');
    const primaryLink = project.videoLink || project.repoLink;
    const primaryIsVideo = Boolean(project.videoLink) || isVideo;

    const handlePointerMove = useCallback((e) => {
        if (prefersReducedMotion || !canUsePointerEffects()) return;

        const card = cardRef.current;
        if (!card) return;

        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const pointerX = x / rect.width;
        const pointerY = y / rect.height;
        const centeredX = pointerX - 0.5;
        const centeredY = pointerY - 0.5;

        rotateX.set(centeredY * -MAX_TILT);
        rotateY.set(centeredX * MAX_TILT);
        depthX.set(centeredX * -PARALLAX_X);
        depthY.set(centeredY * -PARALLAX_Y);
        depthScale.set(1.025);

        card.style.setProperty('--card-pointer-x', `${pointerX * 100}%`);
        card.style.setProperty('--card-pointer-y', `${pointerY * 100}%`);
    }, [depthScale, depthX, depthY, prefersReducedMotion, rotateX, rotateY]);

    const handlePointerLeave = useCallback(() => {
        rotateX.set(0);
        rotateY.set(0);
        depthX.set(0);
        depthY.set(0);
        depthScale.set(1);

        const card = cardRef.current;
        if (card) {
            card.style.setProperty('--card-pointer-x', '50%');
            card.style.setProperty('--card-pointer-y', '50%');
        }
    }, [depthScale, depthX, depthY, rotateX, rotateY]);

    return (
        <motion.article
            ref={cardRef}
            className={`project-card glass-card${project.featured ? ' project-card--featured' : ''}`}
            variants={projectCardReveal}
            whileHover={prefersReducedMotion || !canUsePointerEffects() ? undefined : { y: -5 }}
            transition={{ type: 'spring', stiffness: 180, damping: 28, mass: 0.9 }}
            style={{
                rotateX: smoothRotateX,
                rotateY: smoothRotateY,
                transformPerspective: 1200,
                transformStyle: 'preserve-3d',
            }}
            onPointerMove={handlePointerMove}
            onPointerLeave={handlePointerLeave}
            onClick={onClick}
            role="button"
            tabIndex={0}
            aria-label={`View details for ${title}`}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            <motion.div
                className="project-card__depth"
                aria-hidden="true"
                style={{
                    x: smoothDepthX,
                    y: smoothDepthY,
                    scale: smoothDepthScale,
                }}
            />

            <motion.div
                className="project-card__content"
                style={{
                    x: contentX,
                    y: contentY,
                }}
            >
                {label && <span className="project-card__label">{label}</span>}
                <h3 className="project-card__title">{title}</h3>
                <p className="project-card__impact">{impact}</p>

                <div className="project-card__tags">
                    {project.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="project-card__tag">{tag}</span>
                    ))}
                </div>
            </motion.div>

            <div className="project-card__footer">
                <div className="project-card__links">
                    {primaryLink && (
                        <a
                            href={primaryLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-card__link"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={primaryIsVideo ? 'Watch video' : 'View source code'}
                        >
                            {primaryIsVideo ? (
                                <>
                                    <Play size={14} />
                                    <span>{t.projects.watchVideo}</span>
                                </>
                            ) : (
                                <>
                                    <Github size={14} />
                                    <span>{t.projects.viewCode}</span>
                                </>
                            )}
                        </a>
                    )}

                    {project.websiteLink && (
                        <a
                            href={project.websiteLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-card__link"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="View project website"
                        >
                            <Globe size={14} />
                            <span>{t.projects.viewWebsite}</span>
                        </a>
                    )}
                </div>

                <span className="project-card__detail-hint">
                    <ArrowUpRight size={14} />
                    <span>{t.projects.viewDetails}</span>
                </span>
            </div>
        </motion.article>
    );
}
